// Matching helpers for resolving tracks to Apple Music catalog songs.
//
// Titles for the same recording differ across catalogs — "(feat. X)",
// "(Remastered 2011)", "- Radio Edit" style decorations come and go — so
// candidates are scored with token-overlap similarity across progressively
// cleaned title variants instead of demanding an exact string. The artist
// gate stays hard: covers and karaoke versions often outrank originals in
// search, and a wrong-artist result must never be added to a playlist.

// Loose comparison key: lowercase, strip accents and punctuation.
export function normalizeName(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Same artist test as the server's iTunes pipeline (server/itunes.js):
// exact or substring either way, so "A" matches "A & B" collaborations.
export function artistsMatch(wanted, got) {
  const a = normalizeName(wanted);
  const b = normalizeName(got);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

// --- Title variants (most faithful first) ---

const FEAT_BRACKET_RE = /\s*[([{]\s*(?:feat|ft|featuring|with)[.\s][^)\]}]*[)\]}]?/gi;
const FEAT_TAIL_RE = /\s+(?:feat|ft|featuring)[.\s].*$/i;
const VERSION_WORDS =
  'remaster(?:ed)?|mono|stereo|edit|version|mix|remix|live|acoustic|demo|deluxe|single|explicit|bonus|anniversary|session|radio|extended';
const PAREN_VERSION_RE = new RegExp(
  `\\s*[([{][^)\\]}]*\\b(?:${VERSION_WORDS})\\b[^)\\]}]*[)\\]}]`,
  'gi'
);
const DASH_VERSION_RE = new RegExp(`\\s+[-–—]\\s+[^-–—]*\\b(?:${VERSION_WORDS})\\b.*$`, 'i');

// Progressively simpler forms of a title, deduped: raw, without featured
// artists, without version decorations, and finally without any bracketed
// segment or dash suffix at all. Used both as search queries and as the
// "wanted" side when scoring candidates.
export function titleVariants(title) {
  const variants = [];
  const push = (t) => {
    const v = (t || '').replace(/\s{2,}/g, ' ').trim();
    if (v && !variants.includes(v)) variants.push(v);
  };
  push(title);
  const noFeat = (title || '').replace(FEAT_BRACKET_RE, ' ').replace(FEAT_TAIL_RE, ' ');
  push(noFeat);
  push(noFeat.replace(PAREN_VERSION_RE, ' ').replace(DASH_VERSION_RE, ''));
  push(noFeat.replace(/\s*[([{][^)\]}]*[)\]}]/g, ' ').replace(/\s+[-–—]\s+.*$/, ''));
  return variants;
}

// --- Similarity scoring ---

function tokenSet(s) {
  return new Set(normalizeName(s).split(' ').filter(Boolean));
}

// Dice coefficient over normalized tokens, with a floor when one title is
// fully contained in the other — that shape almost always means the same
// song with extra decoration ("Song" vs "Song (2011 Remaster)").
export function titleSimilarity(a, b) {
  const ta = tokenSet(a);
  const tb = tokenSet(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) if (tb.has(t)) shared += 1;
  const dice = (2 * shared) / (ta.size + tb.size);
  if (shared === ta.size || shared === tb.size) return Math.max(dice, 0.85);
  return dice;
}

// Candidates that are a different product outright, even from a matching
// artist name — never acceptable unless the wanted title says so too.
const REJECT_MARKERS = [
  'karaoke',
  'tribute',
  'instrumental',
  'originally performed',
  'made famous',
  '8 bit',
  'lullaby',
];

// A different recording of the same song — only acceptable when the wanted
// title asks for that version, so penalize below the accept threshold and
// let a proper studio match win instead.
const PENALTY_MARKERS = ['live', 'acoustic', 'remix', 'demo', 'cover', 'sped up', 'slowed'];

function markerMismatch(markers, candidateName, wantedName) {
  const cand = ` ${normalizeName(candidateName)} `;
  const want = ` ${normalizeName(wantedName)} `;
  return markers.some((m) => cand.includes(` ${m} `) && !want.includes(` ${m} `));
}

const MATCH_THRESHOLD = 0.5;

/**
 * Pick the best catalog song for a wanted track.
 * @param {Array<{id, name, artistName}>} candidates - in search-relevance order
 * @param {{name, artistName}} wanted
 * @returns the winning candidate or null — never guesses across artists.
 */
export function pickBestSongMatch(candidates, wanted) {
  const variants = titleVariants(wanted.name);
  let best = null;
  for (const candidate of candidates) {
    if (!artistsMatch(wanted.artistName, candidate.artistName)) continue;
    if (markerMismatch(REJECT_MARKERS, candidate.name, wanted.name)) continue;
    let score = 0;
    for (const v of variants) {
      score = Math.max(score, titleSimilarity(v, candidate.name));
    }
    if (markerMismatch(PENALTY_MARKERS, candidate.name, wanted.name)) score *= 0.5;
    // Strict > keeps the earlier (more relevant) result on ties.
    if (score >= MATCH_THRESHOLD && (!best || score > best.score)) {
      best = { candidate, score };
    }
  }
  return best ? best.candidate : null;
}

// Canonical Apple Music song URLs (/album/{slug}/{albumId}?i={songId}) carry
// the catalog song ID as the `i` query param — the same ID the Apple Music
// API accepts when adding tracks to a library playlist.
export function parseCatalogSongId(url) {
  if (!url) return null;
  try {
    return new URL(url).searchParams.get('i') || null;
  } catch {
    return null;
  }
}
